import { CATEGORIES } from "@/lib/categories";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProductForm } from "@/pages/admin/ProductForm";
import { HeroManager } from "@/pages/admin/HeroManager";
import { OrdersPanel } from "@/pages/admin/OrdersPanel";
import { ShippingPanel, ContentPanel, LegalPanel, DiscountsPanel } from "@/pages/admin/StorePanels";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Archive, RotateCcw } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    if (user) load();
  }, [user, load, navigate]);

  if (user === null) return <div className="pt-40 text-center text-white/40 font-mono-label text-sm uppercase tracking-luxe">Loading…</div>;
  if (!user) return null;

  const remove = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleArchive = async (p) => {
    try {
      await api.patch(`/admin/products/${p.id}/archive`, { archived: !p.archived });
      toast.success(p.archived ? "Product restored" : "Product archived");
      load();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  return (
    <div className="pt-24 lg:pt-28 pb-24 max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-3">Dashboard</p>
          <h1 className="font-serif-display text-4xl lg:text-5xl font-light">Store Management</h1>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/25 px-5 py-3 font-mono-label text-xs uppercase tracking-luxe text-white/70 hover:text-white">
            <ExternalLink size={14} /> View Store
          </a>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 border border-white/25 px-5 py-3 font-mono-label text-xs uppercase tracking-luxe text-white/70 hover:text-white">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-10 overflow-x-auto">
        {[
          { k: "products", label: "Products" },
          { k: "orders", label: "Orders" },
          { k: "discounts", label: "Discounts" },
          { k: "hero", label: "Hero Video" },
          { k: "content", label: "Content" },
          { k: "shipping", label: "Shipping" },
          { k: "legal", label: "Legal" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-6 py-4 whitespace-nowrap font-mono-label text-xs uppercase tracking-luxe border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-white text-white" : "border-transparent text-white/40 hover:text-white/70"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <>
          <div className="flex justify-between items-center mb-8">
            <span className="font-mono-label text-xs uppercase tracking-luxe text-white/40">{products.length} products</span>
            <button
              data-testid="add-product-btn"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="flex items-center gap-2 bg-white text-black px-6 py-3 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white"
            >
              <Plus size={15} /> Add Product
            </button>
          </div>

          <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-product-list">
            {products.length === 0 && (
              <p className="p-10 text-center text-white/40 font-mono-label text-sm uppercase tracking-luxe">No products yet</p>
            )}
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className={`flex items-center gap-4 p-4 ${p.archived ? "opacity-50" : ""}`}>
                <div className="w-14 h-16 bg-neutral-900 shrink-0 overflow-hidden">
                  {p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white truncate">{p.name}</p>
                  <p className="font-mono-label text-xs text-white/40 mt-1">
                    {p.category}{!CATEGORIES.includes(p.category) && <span data-testid={`legacy-category-${p.id}`} className="text-amber-300/80"> (old category — choose a new one)</span>} · {formatPrice(p.price)}{p.price_confirmed === false && <span data-testid={`price-check-${p.id}`} className="text-amber-300/80"> (check price — open and save to confirm)</span>} · Size {p.size || "—"}
                    {!p.published && <span className="text-yellow-500/80"> · Draft</span>}
                    {p.featured && <span className="text-white/70"> · Featured</span>}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span
                      data-testid={`availability-${p.id}`}
                      className={`font-mono-label text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
                        p.sold ? "border-red-500/50 text-red-400" : "border-green-500/40 text-green-400"
                      }`}
                    >
                      {p.sold ? "Sold Out" : "Available"}
                    </span>
                    {p.archived && (
                      <span data-testid={`archived-badge-${p.id}`} className="font-mono-label text-[10px] uppercase tracking-wider px-2 py-0.5 border border-white/30 text-white/50">
                        Archived · Hidden
                      </span>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`archive-product-${p.id}`}
                  onClick={() => toggleArchive(p)}
                  title={p.archived ? "Restore to store" : "Hide from store"}
                  className="p-2 text-white/50 hover:text-white"
                >
                  {p.archived ? <RotateCcw size={16} strokeWidth={1.4} /> : <Archive size={16} strokeWidth={1.4} />}
                </button>
                <button data-testid={`edit-product-${p.id}`} onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 text-white/50 hover:text-white">
                  <Pencil size={16} strokeWidth={1.4} />
                </button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="p-2 text-white/50 hover:text-red-400">
                  <Trash2 size={16} strokeWidth={1.4} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "hero" && <HeroManager />}
      {tab === "orders" && <OrdersPanel />}
      {tab === "discounts" && <DiscountsPanel />}
      {tab === "content" && <ContentPanel />}
      {tab === "shipping" && <ShippingPanel />}
      {tab === "legal" && <LegalPanel />}

      {showForm && (
        <ProductForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}
